import React from 'react';

import { Box, Center, Stack, Text, UnstyledButton, createStyles } from '@mantine/core';
import { IconBarbell } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { WorkoutCalendarKiloClassType } from 'types';

import WeekEventCard from './WeekEventCard';

interface WeekDayColumnProps {
  date: string;
  classTypes: WorkoutCalendarKiloClassType[];
  isToday: boolean;
  onHeaderClick: (date: string) => void;
}

const COLUMN_MIN_HEIGHT = 460;

const useStyles = createStyles((theme, { isToday }: { isToday: boolean }) => ({
  column: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: COLUMN_MIN_HEIGHT,
    minWidth: 0,
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.md,
    overflow: 'hidden',
    backgroundColor: theme.white,
  },
  header: {
    padding: theme.spacing.sm,
    textAlign: 'left',
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    backgroundColor: isToday ? theme.colors.kiloBlue[6] : theme.colors.gray[0],
    color: isToday ? theme.white : theme.colors.gray[7],
    '&:hover': { backgroundColor: isToday ? theme.colors.kiloBlue[7] : theme.colors.gray[1] },
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing.xs,
  },
  empty: {
    height: '100%',
    color: theme.colors.gray[5],
  },
}));

const WeekDayColumn = ({ date, classTypes, isToday, onHeaderClick }: WeekDayColumnProps): JSX.Element => {
  const { classes } = useStyles({ isToday });
  const d = dayjs(date);

  return (
    <Box className={classes.column}>
      <UnstyledButton
        className={classes.header}
        onClick={() => onHeaderClick(date)}
        data-cy='weekDayHeader'
        aria-label={`Open ${d.format('dddd, MMMM D')} in day view`}
      >
        <Text size='xs' fw={600}>
          {d.format('ddd')}
        </Text>
        <Text size='lg' fw={700}>
          {d.format('D')}
        </Text>
      </UnstyledButton>

      <Box className={classes.body}>
        {classTypes.length === 0 ? (
          <Center className={classes.empty}>
            <Stack align='center' spacing={4}>
              <IconBarbell size={28} stroke={1.5} />
              <Text size='sm'>No classes</Text>
            </Stack>
          </Center>
        ) : (
          <Stack spacing='xs'>
            {classTypes.map(ct => (
              <WeekEventCard key={ct.kiloClassTypeId} classType={ct} date={date} />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default WeekDayColumn;
